// Header/idempotency mechanics for the Slot Availability Calendar's write
// protocol (backend doc section 4: "Revisions, retries and atomic writes").
//
// Deliberately separate from SlotCalendarApiService's own error-code map
// (SLOT_CALENDAR_ERROR_CODES / toFriendlySlotCalendarError, defined in that
// file per this repo's DineInApiService convention) — this file only builds
// the request-side headers and reads the response-side ones, it doesn't know
// about friendly copy.

/**
 * Mints a fresh Idempotency-Key. Call this ONCE per user intent (e.g. the tap
 * on "Save booking") and reuse the SAME value for every automatic retry of
 * that one intent (a network error, a 503) — minting a new key on retry
 * defeats the whole mechanism, since the backend's replay protection is keyed
 * on this value matching the original request's actor+hash.
 */
export function mintIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Header for a write against an EXISTING calendar/slot/entry — the calendar's
 *  current `ETag` (format "cal-{calendarId}-r{revision}") echoed back so the
 *  server can detect a stale edit. Missing this header is a 428; a stale
 *  value is a 412. */
export function buildIfMatchHeaders(etag: string, idempotencyKey: string): Record<string, string> {
  return {
    "If-Match": etag,
    "Idempotency-Key": idempotencyKey,
  };
}

/** Header for CREATING the calendar itself — the one write in this contract
 *  that has no prior revision to match against. */
export function buildIfNoneMatchCreateHeaders(idempotencyKey: string): Record<string, string> {
  return {
    "If-None-Match": "*",
    "Idempotency-Key": idempotencyKey,
  };
}

/** Reads the calendar's current concurrency token off an owner response.
 *  Every owner GET and every mutation response carries this — null only
 *  means the header genuinely wasn't sent (a public/customer-facing
 *  response, or a productless preview), never a parsing failure. */
export function parseCalendarEtag(response: Response): string | null {
  const etag = response.headers.get("ETag");
  // gzip can weaken the ETag to W/"..."; until the backend's weak-ETag
  // acceptance is deployed, strip the prefix and otherwise keep it byte for
  // byte (never re-quote or rebuild it).
  return etag ? etag.replace(/^W\//, "") : null;
}

/**
 * Parses `calendarRevision` out of an ETag of the documented
 * "cal-{calendarId}-r{revision}" form. Returns null for anything else
 * (including a missing header) rather than throwing — callers already treat
 * "no revision known yet" as a valid state (a calendar that doesn't exist
 * yet has none), so a malformed/unexpected ETag degrades the same way rather
 * than crashing the read that surfaced it.
 */
export function parseCalendarRevisionFromEtag(etag: string | null): number | null {
  if (!etag) return null;
  const match = /-r(\d+)$/.exec(etag);
  if (!match) return null;
  const revision = parseInt(match[1], 10);
  return Number.isFinite(revision) ? revision : null;
}

/** Reads the created resource's id out of a 201 response's `Location`
 *  header, when present. Only ever a fallback — SlotCalendarMutationResult's
 *  own `resourceId` field (in the response body) is the primary source, this
 *  exists purely because the backend doc calls out Location as part of the
 *  contract ("Create requests return 201 and Location for the created
 *  resource") and a caller may want to cross-check the two agree. */
export function parseLocationResourceId(response: Response): string | null {
  const location = response.headers.get("Location");
  if (!location) return null;
  const segments = location.split("/").filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : null;
}
